import { useEffect, useRef, useState } from 'react';
import type {
  GoogleDriveClientFolderCheckView,
  GoogleDriveClientFolderDiagnostic,
} from '@tosho/contracts';
import { clientDirectoryApi, type ClientRecord } from './client-directory-api';
import { googleDriveApi, GoogleDriveApiError } from './google-drive-api';

export const clientFolderCheckLabels: Readonly<
  Record<GoogleDriveClientFolderCheckView['status'], string>
> = {
  RUNNING: 'Vérification des dossiers en cours.',
  VERIFIED: 'Dossiers privés vérifiés à la date du contrôle.',
  NOT_VERIFIED: 'Dossiers non vérifiés. Vous pouvez reprendre la préparation.',
  INTERRUPTED: 'Essai interrompu, sans résultat final. Vous pouvez reprendre la préparation.',
};
const dateFormatter = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
type ClientChoice = Pick<ClientRecord, 'id' | 'displayName'>;

async function readClientCheck(clientId: string): Promise<GoogleDriveClientFolderCheckView | null> {
  const result = await googleDriveApi.readClientFolders(clientId);
  if (result !== null && result.clientId !== clientId) throw new GoogleDriveApiError('UNAVAILABLE');
  return result;
}

/** Un choix explicite, jamais le premier Client par défaut. Le résultat vient de la base,
 * pas d'un état local de succès ; aucun appel Google au chargement ou au changement de Client. */
export function DriveClientFolderCheckCard({
  csrfToken,
  enabled,
  onRefresh,
  onReauthenticate,
}: {
  readonly csrfToken: string;
  readonly enabled: boolean;
  readonly onRefresh: () => Promise<void>;
  readonly onReauthenticate: () => void;
}): React.JSX.Element {
  const [query, setQuery] = useState('');
  const [clients, setClients] = useState<readonly ClientChoice[]>([]);
  const [limited, setLimited] = useState(false);
  const [selected, setSelected] = useState<ClientChoice | null>(null);
  const [view, setView] = useState<GoogleDriveClientFolderCheckView | null>(null);
  const [searching, setSearching] = useState(false);
  const [reading, setReading] = useState(false);
  const [known, setKnown] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [diagnostic, setDiagnostic] = useState<GoogleDriveClientFolderDiagnostic | null>(null);
  const [reauth, setReauth] = useState(false);
  const generation = useRef(0);
  const running = useRef(false);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      generation.current++;
    };
  }, []);

  async function search(value: string): Promise<void> {
    const request = ++generation.current;
    setSearching(true);
    setSelected(null);
    setView(null);
    setKnown(false);
    setError(null);
    setDiagnostic(null);
    try {
      const result = await clientDirectoryApi.search(value, 0, 'clients');
      if (mounted.current && request === generation.current) {
        setClients(
          result.clients.filter(
            (client) => client.stage === 'CLIENT' && client.origin !== 'FREEBE_LEGACY_UNREVIEWED',
          ),
        );
        setLimited(result.clients.length >= 50);
      }
    } catch {
      if (mounted.current && request === generation.current) {
        setClients([]);
        setError('Les Clients ne peuvent pas être chargés. Relancez la recherche.');
      }
    } finally {
      if (mounted.current && request === generation.current) setSearching(false);
    }
  }
  useEffect(() => {
    if (enabled) void search('');
    return () => {
      generation.current++;
    };
  }, [enabled]);
  useEffect(() => {
    setView(null);
    setKnown(false);
    setReauth(false);
    setDiagnostic(null);
    if (!selected) {
      setReading(false);
      return;
    }
    let active = true;
    setReading(true);
    setError(null);
    void readClientCheck(selected.id)
      .then(
        (result) => {
          if (active) {
            setView(result);
            setKnown(true);
          }
        },
        () => {
          if (active)
            setError('Le dernier résultat ne peut pas être chargé. Rechargez cette page.');
        },
      )
      .finally(() => {
        if (active) setReading(false);
      });
    return () => {
      active = false;
    };
  }, [selected]);
  useEffect(() => {
    if (!selected || view?.status !== 'RUNNING' || busy) return;
    let active = true;
    const timer = window.setInterval(() => {
      void readClientCheck(selected.id)
        .then(async (result) => {
          if (active) {
            setView(result);
            await onRefresh();
          }
        })
        .catch(() => {
          if (active) setError('Le résultat ne peut pas être actualisé. Rechargez cette page.');
        });
    }, 5000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [selected, view?.status, busy, onRefresh]);

  async function prepare(): Promise<void> {
    if (
      !selected ||
      !enabled ||
      !known ||
      reading ||
      running.current ||
      searching ||
      view?.status === 'RUNNING'
    )
      return;
    running.current = true;
    setBusy(true);
    setError(null);
    setDiagnostic(null);
    setReauth(false);
    try {
      await googleDriveApi.prepareClientFolders(csrfToken, selected.id);
    } catch (failure) {
      if (mounted.current) {
        setDiagnostic(failure instanceof GoogleDriveApiError ? failure.diagnostic : null);
        const recent =
          failure instanceof GoogleDriveApiError && failure.code === 'REAUTHENTICATION_REQUIRED';
        setReauth(recent);
        setError(
          recent
            ? 'Reconnectez-vous à Tosho puis recommencez.'
            : 'La préparation n’a pas pu être confirmée. Consultez le résultat enregistré avant de reprendre.',
        );
      }
    } finally {
      // Une réponse POST perdue peut cacher une réussite : relire la tentative, sans la recréer.
      await readClientCheck(selected.id).then(
        (result) => {
          if (mounted.current) {
            setView(result);
            setKnown(true);
          }
        },
        () => {
          if (mounted.current) {
            setKnown(false);
            setError('Le résultat enregistré ne peut pas être chargé. Rechargez cette page.');
          }
        },
      );
      if (mounted.current) {
        await onRefresh().catch(() =>
          setError('L’historique ne peut pas être actualisé. Rechargez cette page.'),
        );
        setBusy(false);
      }
      running.current = false;
    }
  }
  return (
    <section
      className="drive-integration__card drive-integration__client-card"
      id="drive-client-folders"
      aria-labelledby="drive-client-folders-title"
      aria-busy={busy || reading || searching}
    >
      <header>
        <p className="dashboard-panel__eyebrow">Préparation du stockage</p>
        <h2 id="drive-client-folders-title">Dossiers privés d’un Client</h2>
      </header>
      <p>
        Choisissez un Client existant. Tosho prépare ses dossiers privés vides sur le Drive de
        l’agence, ou revérifie les mêmes dossiers s’ils existent déjà.
      </p>
      <p>
        Aucun fichier n’est déposé. Cette vérification n’active pas encore les dépôts métier ni les
        accès des Clients.
      </p>
      <p>Les anciens liens Freebe doivent d’abord être confirmés dans le répertoire Clients.</p>
      {enabled ? (
        <>
          <form
            className="drive-integration__client-search"
            onSubmit={(event) => {
              event.preventDefault();
              if (!busy && !searching) void search(query);
            }}
          >
            <label htmlFor="drive-client-query">Rechercher un Client</label>
            <div className="drive-integration__actions">
              <input
                id="drive-client-query"
                type="search"
                value={query}
                maxLength={200}
                disabled={busy || searching}
                onChange={(event) => setQuery(event.target.value)}
              />
              <button type="submit" className="is-secondary" disabled={busy || searching}>
                {searching ? 'Recherche…' : 'Rechercher'}
              </button>
            </div>
            <label htmlFor="drive-client-choice">Client à préparer</label>
            <select
              id="drive-client-choice"
              value={selected?.id ?? ''}
              disabled={busy || searching || !clients.length}
              onChange={(event) => {
                setKnown(false);
                setView(null);
                setSelected(clients.find((client) => client.id === event.target.value) ?? null);
              }}
            >
              <option value="">Choisir un Client</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.displayName}
                </option>
              ))}
            </select>
            {limited ? (
              <p>Les 50 premiers résultats sont affichés. Précisez la recherche si nécessaire.</p>
            ) : null}
            {!searching && !clients.length ? <p>Aucun Client trouvé.</p> : null}
          </form>
        </>
      ) : (
        <p>Une connexion Google Drive active est nécessaire pour préparer les dossiers.</p>
      )}
      <button
        type="button"
        disabled={
          !enabled ||
          !selected ||
          !known ||
          busy ||
          reading ||
          searching ||
          view?.status === 'RUNNING'
        }
        onClick={() => void prepare()}
      >
        {busy
          ? 'Vérification en cours…'
          : view
            ? 'Revérifier les dossiers'
            : 'Préparer les dossiers'}
      </button>
      {error ? (
        <p
          className="tosho-feedback is-error"
          role="alert"
          data-drive-folder-diagnostic={diagnostic ? JSON.stringify(diagnostic) : undefined}
        >
          {error}
        </p>
      ) : null}
      {reauth ? (
        <button type="button" onClick={onReauthenticate}>
          Me reconnecter à Tosho
        </button>
      ) : null}
      <p role="status">
        {reading
          ? 'Chargement du dernier résultat…'
          : selected && !known
            ? 'Dernier résultat non vérifié.'
            : view
              ? clientFolderCheckLabels[view.status]
              : selected
                ? 'Aucune tentative enregistrée pour ce Client.'
                : 'Choisissez un Client pour consulter son dernier résultat.'}
      </p>
      {view?.checkedAt ? (
        <p>
          Dernier contrôle : {dateFormatter.format(new Date(view.checkedAt))}. Ce constat n’est pas
          une garantie permanente d’accès.
        </p>
      ) : null}
      <p>
        En cas d’interruption, reprenez la préparation : Tosho conserve les mêmes références, sans
        déplacer les fichiers ni choisir un autre Drive.
      </p>
    </section>
  );
}
